import { useEffect, useCallback } from 'preact/hooks';
import { signal, computed } from '@preact/signals';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Button, Select } from '../components/ui';

// ══════════════════════════════════════════════════════════════
// TYPES (matching llm_usage_logs schema)
// ══════════════════════════════════════════════════════════════

interface Totals {
  requests: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  total_cost: number;
  avg_latency_ms: number;
  errors: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
}

interface ByItem { requests: number; tokens: number; cost: number }
interface ByModel extends ByItem { model: string }
interface ByProvider extends ByItem { provider: string }
interface ByAgent extends ByItem { agent: string }
interface ByChannel extends ByItem { channel: string }
interface ByStopReason { stop_reason: string; count: number }
interface TopTool { tool: string; count: number }
interface DailyItem { date: string; requests: number; tokens: number; cost: number; errors: number }
interface HourlyItem { hour: number; requests: number; tokens: number }
interface DowItem { dow: number; requests: number; tokens: number }

interface Summary {
  totals: Totals;
  by_model: ByModel[];
  by_provider: ByProvider[];
  by_agent: ByAgent[];
  by_channel: ByChannel[];
  by_stop_reason: ByStopReason[];
  top_tools: TopTool[];
  daily: DailyItem[];
  hourly: HourlyItem[];
  dow: DowItem[];
}

interface Session {
  session_key: string;
  customer_id: string;
  customer_email: string;
  machine_name: string;
  requests: number;
  tokens: number;
  cost: number;
  errors: number;
  last_activity: string;
  first_activity: string;
  models_used: string;
  avg_latency_ms: number;
}

interface SessionLog {
  id: string;
  created_at: string;
  model: string;
  provider: string;
  role: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  cost_total: number;
  duration_ms: number;
  agent_id: string | null;
  channel: string | null;
  stop_reason: string | null;
  tool_names: string | null;
  tool_call_count: number;
  error_count: number;
}

interface TimeseriesPoint {
  timestamp: string;
  input_tokens: number;
  output_tokens: number;
  cost: number;
  cumulative_tokens: number;
  cumulative_cost: number;
}

interface FilterOptions {
  models: string[];
  providers: string[];
  channels: string[];
  customers: Array<{ id: string; email: string }>;
  machines: Array<{ id: string; name: string }>;
}

// ══════════════════════════════════════════════════════════════
// STATE
// ══════════════════════════════════════════════════════════════

const loading = signal(true);
const error = signal('');
const summary = signal<Summary | null>(null);
const sessions = signal<Session[]>([]);
const sessionsTotal = signal(0);
const filterOptions = signal<FilterOptions | null>(null);

// Filters
const dateRange = signal<'7d' | '30d' | '90d' | 'custom'>('30d');
const startDate = signal('');
const endDate = signal('');
const filterModel = signal('');
const filterProvider = signal('');
const filterCustomer = signal('');
const filterMachine = signal('');
const filterChannel = signal('');
const searchQuery = signal('');
const viewMode = signal<'tokens' | 'cost'>('cost');
const sessionsSort = signal<'cost' | 'tokens' | 'requests' | 'recent'>('cost');

// Session drilldown
const selectedSession = signal<string | null>(null);
const sessionLogs = signal<SessionLog[]>([]);
const sessionTimeseries = signal<TimeseriesPoint[]>([]);
const sessionLoading = signal(false);

// ══════════════════════════════════════════════════════════════
// UTILS
// ══════════════════════════════════════════════════════════════

function formatTokens(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(n);
}

function formatUsd(n: number): string {
  if (n >= 100) return '$' + n.toFixed(0);
  if (n >= 1) return '$' + n.toFixed(2);
  if (n >= 0.01) return '$' + n.toFixed(3);
  return '$' + n.toFixed(4);
}

function formatMs(ms: number): string {
  if (!ms) return '—';
  if (ms >= 60000) return (ms / 60000).toFixed(1) + 'min';
  if (ms >= 1000) return (ms / 1000).toFixed(1) + 's';
  return ms + 'ms';
}

function getDateRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const end = now.toISOString().split('T')[0] || '';
  if (dateRange.value === 'custom') return { startDate: startDate.value, endDate: endDate.value || end };
  const days = dateRange.value === '7d' ? 7 : dateRange.value === '90d' ? 90 : 30;
  const start = new Date(now.getTime() - days * 86400000).toISOString().split('T')[0] || '';
  return { startDate: start, endDate: end };
}

function buildQueryString(): string {
  const { startDate: sd, endDate: ed } = getDateRange();
  const params = new URLSearchParams();
  if (sd) params.set('startDate', sd);
  if (ed) params.set('endDate', ed);
  if (filterModel.value) params.set('model', filterModel.value);
  if (filterProvider.value) params.set('provider', filterProvider.value);
  if (filterCustomer.value) params.set('customer_id', filterCustomer.value);
  if (filterMachine.value) params.set('machine_id', filterMachine.value);
  if (filterChannel.value) params.set('channel', filterChannel.value);
  if (searchQuery.value) params.set('search', searchQuery.value);
  return params.toString();
}

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ══════════════════════════════════════════════════════════════
// DATA FETCHING
// ══════════════════════════════════════════════════════════════

async function loadAll() {
  loading.value = true;
  error.value = '';
  try {
    const qs = buildQueryString();
    const [summaryRes, sessionsRes, filtersRes] = await Promise.all([
      api.get<{ summary: Summary }>(`/admin/usage/summary?${qs}`),
      api.get<{ sessions: Session[]; total: number }>(`/admin/usage/sessions?${qs}&sort=${sessionsSort.value}&limit=50`),
      filterOptions.value ? Promise.resolve(null) : api.get<{ filters: FilterOptions }>('/admin/usage/filters'),
    ]);
    summary.value = summaryRes.summary;
    sessions.value = sessionsRes.sessions;
    sessionsTotal.value = sessionsRes.total;
    if (filtersRes) filterOptions.value = filtersRes.filters;
  } catch (e: any) {
    error.value = e.message || 'Failed to load usage data';
  } finally {
    loading.value = false;
  }
}

async function loadSessionDetail(key: string) {
  sessionLoading.value = true;
  selectedSession.value = key;
  try {
    const qs = buildQueryString();
    const res = await api.get<{ detail: { logs: SessionLog[]; timeseries: TimeseriesPoint[] } }>(`/admin/usage/sessions/${encodeURIComponent(key)}?${qs}`);
    sessionLogs.value = res.detail.logs;
    sessionTimeseries.value = res.detail.timeseries;
  } catch (e: any) {
    error.value = e.message;
  } finally {
    sessionLoading.value = false;
  }
}

async function handleExport() {
  const qs = buildQueryString();
  window.open(`/api/v1/admin/usage/export?${qs}`, '_blank');
}

// ══════════════════════════════════════════════════════════════
// STYLES
// ══════════════════════════════════════════════════════════════

const S = {
  page: { padding: '24px', maxWidth: 1400, margin: '0 auto', color: T.text } as const,
  h1: { fontSize: 24, fontWeight: 700, marginBottom: 4, color: T.text } as const,
  subtitle: { fontSize: 13, color: T.textSecondary, marginBottom: 20 } as const,

  filterBar: { display: 'flex', flexWrap: 'wrap' as const, gap: 8, marginBottom: 20, alignItems: 'center' },
  filterBtn: (active: boolean) => ({
    padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
    background: active ? T.primary : T.bgSecondary, color: active ? '#fff' : T.textMuted,
  }),
  filterSelect: { padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.borderHover}`, background: T.bg, color: T.textSecondary, fontSize: 12, minWidth: 120 } as const,
  filterInput: { padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.borderHover}`, background: T.bg, color: T.textSecondary, fontSize: 12, width: 160 } as const,
  exportBtn: { padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.primary}`, background: `transparent`, color: T.primary, cursor: 'pointer', fontSize: 12, marginLeft: 'auto' } as const,

  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 24 } as const,
  card: { background: T.bgSecondary, borderRadius: 10, padding: '14px 16px', border: `1px solid ${T.border}` } as const,
  cardLabel: { fontSize: 11, color: T.textSecondary, marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.5px' } as const,
  cardValue: { fontSize: 22, fontWeight: 700, color: T.text } as const,
  cardSub: { fontSize: 11, color: T.textMuted, marginTop: 2 } as const,

  insightsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 24 } as const,
  insightCard: { background: T.bgSecondary, borderRadius: 10, padding: 16, border: `1px solid ${T.border}` } as const,
  insightTitle: { fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 10 } as const,
  insightRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 12 } as const,
  insightLabel: { color: T.textSecondary } as const,
  insightValue: { color: T.text, fontWeight: 600 } as const,
  barBg: { height: 4, background: T.bgSecondary, borderRadius: 2, marginTop: 2 } as const,

  heatmapSection: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 } as const,
  heatmapCard: { background: T.bgSecondary, borderRadius: 10, padding: 16, border: `1px solid ${T.border}` } as const,
  heatmapGrid: { display: 'grid', gap: 3 } as const,
  heatmapCell: (intensity: number) => ({
    borderRadius: 3, padding: '6px 4px', textAlign: 'center' as const, fontSize: 10, color: intensity > 0.5 ? '#fff' : T.textSecondary,
    background: intensity > 0 ? `rgba(59, 130, 246, ${Math.max(0.15, intensity)})` : '#0d0d1a',
  }),

  dailyChart: { background: T.bgSecondary, borderRadius: 10, padding: 16, border: `1px solid ${T.border}`, marginBottom: 24 } as const,
  barChart: { display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, padding: '8px 0' } as const,
  bar: (h: number, color: string) => ({
    flex: 1, minWidth: 4, maxWidth: 20, height: `${h}%`, background: color, borderRadius: '2px 2px 0 0', transition: 'height 0.3s',
  }),

  sessionsList: { background: T.bgSecondary, borderRadius: 10, padding: 16, border: `1px solid ${T.border}`, marginBottom: 24 } as const,
  sessionRow: (selected: boolean) => ({
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px', gap: 8, padding: '10px 8px',
    borderBottom: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 12, alignItems: 'center',
    background: selected ? '#1a1a3e' : 'transparent', borderRadius: selected ? 6 : 0,
  }),
  sessionHeader: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px', gap: 8, padding: '8px', fontSize: 11, color: T.textMuted, borderBottom: `1px solid ${T.borderHover}`, textTransform: 'uppercase' as const } as const,

  drilldown: { background: T.bgSecondary, borderRadius: 10, padding: 16, border: `1px solid ${T.border}`, marginBottom: 24 } as const,
  logRow: (isError: boolean) => ({
    display: 'grid', gridTemplateColumns: '140px 1fr 80px 80px 80px 80px', gap: 8, padding: '6px 8px', fontSize: 11,
    borderBottom: '1px solid #0d0d1a', color: isError ? T.danger : T.textSecondary,
  }),

  sectionTitle: { fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 12 } as const,
  badge: (color: string) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
    background: color + '22', color: color, marginRight: 4,
  }),
};

// ══════════════════════════════════════════════════════════════
// COMPONENTS
// ══════════════════════════════════════════════════════════════

function FilterBar() {
  return (
    <div style={S.filterBar}>
      {(['7d', '30d', '90d'] as const).map(r => (
        <Button key={r} variant={dateRange.value === r ? 'primary' : 'ghost'} size="sm" onClick={() => { dateRange.value = r; loadAll(); }}>
          {r === '7d' ? '7 Days' : r === '30d' ? '30 Days' : '90 Days'}
        </Button>
      ))}

      <Button variant={dateRange.value === 'custom' ? 'primary' : 'ghost'} size="sm" onClick={() => { dateRange.value = 'custom'; }}>Custom</Button>
      {dateRange.value === 'custom' && (
        <>
          <input type="date" style={S.filterInput} value={startDate.value} onChange={(e: any) => startDate.value = e.target.value} />
          <input type="date" style={S.filterInput} value={endDate.value} onChange={(e: any) => { endDate.value = e.target.value; loadAll(); }} />
        </>
      )}

      <div style={{ width: 1, height: 24, background: '#2a2a4a' }} />

      {filterOptions.value && (
        <>
          <Select style={S.filterSelect} value={filterModel.value} onChange={(v) => { filterModel.value = v; loadAll(); }}
            options={[{ value: '', label: 'All Models' }, ...filterOptions.value.models.map(m => ({ value: m, label: m }))]} />
          <Select style={S.filterSelect} value={filterProvider.value} onChange={(v) => { filterProvider.value = v; loadAll(); }}
            options={[{ value: '', label: 'All Providers' }, ...filterOptions.value.providers.map(p => ({ value: p, label: p }))]} />
          <Select style={S.filterSelect} value={filterCustomer.value} onChange={(v) => { filterCustomer.value = v; loadAll(); }}
            options={[{ value: '', label: 'All Customers' }, ...filterOptions.value.customers.map(c => ({ value: c.id, label: c.email }))]} />
          {filterOptions.value.machines.length > 0 && (
            <Select style={S.filterSelect} value={filterMachine.value} onChange={(v) => { filterMachine.value = v; loadAll(); }}
              options={[{ value: '', label: 'All Machines' }, ...filterOptions.value.machines.map(m => ({ value: m.id, label: m.name }))]} />
          )}
          {filterOptions.value.channels.length > 0 && (
            <Select style={S.filterSelect} value={filterChannel.value} onChange={(v) => { filterChannel.value = v; loadAll(); }}
              options={[{ value: '', label: 'All Channels' }, ...filterOptions.value.channels.map(ch => ({ value: ch, label: ch }))]} />
          )}
        </>
      )}

      <input
        type="text" placeholder="Search agent, model, session..."
        style={S.filterInput}
        value={searchQuery.value}
        onKeyDown={(e: any) => { if (e.key === 'Enter') loadAll(); }}
        onChange={(e: any) => searchQuery.value = e.target.value}
      />

      <div style={{ display: 'flex', gap: 4 }}>
 <Button variant={viewMode.value === 'cost' ? 'primary' : 'ghost'} size="sm" onClick={() => viewMode.value = 'cost'}>Cost</Button>
 <Button variant={viewMode.value === 'tokens' ? 'primary' : 'ghost'} size="sm" onClick={() => viewMode.value = 'tokens'}>Tokens</Button>
      </div>

 <Button variant="ghost" size="sm" onClick={handleExport} style={{ border: `1px solid ${T.primary}`, color: T.primary, marginLeft: 'auto' }}>Export CSV</Button>
    </div>
  );
}

function OverviewCards() {
  const s = summary.value;
  if (!s) return null;
  const t = s.totals;
  const errorRate = t.requests > 0 ? ((t.errors / t.requests) * 100).toFixed(1) : '0';
  const avgTokensPerReq = t.requests > 0 ? Math.round(t.total_tokens / t.requests) : 0;
  const avgCostPerReq = t.requests > 0 ? t.total_cost / t.requests : 0;
  const cacheHitRate = t.total_tokens > 0 ? ((t.cache_read_tokens / t.total_tokens) * 100).toFixed(1) : '0';

  const cards = [
    { label: 'Total Cost', value: formatUsd(t.total_cost), sub: `${t.requests} requests` },
    { label: 'Total Tokens', value: formatTokens(t.total_tokens), sub: `in: ${formatTokens(t.input_tokens)} / out: ${formatTokens(t.output_tokens)}` },
    { label: 'Requests', value: t.requests.toLocaleString(), sub: `${s.daily.length} active days` },
    { label: 'Avg Latency', value: formatMs(t.avg_latency_ms), sub: 'per request' },
    { label: 'Errors', value: String(t.errors), sub: `${errorRate}% error rate`, color: t.errors > 0 ? T.danger : undefined },
    { label: 'Cache Hit Rate', value: `${cacheHitRate}%`, sub: `read: ${formatTokens(t.cache_read_tokens)} / write: ${formatTokens(t.cache_write_tokens)}`, color: '#06b6d4' },
    { label: 'Avg Tokens/Req', value: formatTokens(avgTokensPerReq), sub: '' },
    { label: 'Avg Cost/Req', value: formatUsd(avgCostPerReq), sub: '' },
    { label: 'Sessions', value: String(sessionsTotal.value), sub: 'unique sessions' },
    { label: 'Models Used', value: String(s.by_model.length), sub: s.by_model[0]?.model || '' },
  ];

  return (
    <div style={S.cardsGrid}>
      {cards.map((c, i) => (
        <div key={i} style={S.card}>
          <div style={S.cardLabel}>{c.label}</div>
          <div style={{ ...S.cardValue, color: (c as any).color || '#fff' }}>{c.value}</div>
          <div style={S.cardSub}>{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function TokensByTypeChart() {
  const s = summary.value;
  if (!s) return null;
  const t = s.totals;
  const total = t.input_tokens + t.output_tokens + t.cache_read_tokens + t.cache_write_tokens || 1;
  const segments = [
    { label: 'Input', value: t.input_tokens, color: T.primary },
    { label: 'Output', value: t.output_tokens, color: T.success },
    { label: 'Cache Read', value: t.cache_read_tokens, color: '#06b6d4' },
    { label: 'Cache Write', value: t.cache_write_tokens, color: T.warning },
  ];

  return (
    <div style={{ ...S.insightCard, marginBottom: 24 }}>
 <div style={S.insightTitle}>Tokens by Type</div>
      <div style={{ display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
        {segments.filter(s => s.value > 0).map(seg => (
          <div
            key={seg.label}
            style={{ width: `${(seg.value / total) * 100}%`, background: seg.color, minWidth: 2 }}
            title={`${seg.label}: ${formatTokens(seg.value)} (${((seg.value / total) * 100).toFixed(1)}%)`}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {segments.map(seg => (
          <div key={seg.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: seg.color }} />
            <span style={{ color: T.textSecondary }}>{seg.label}:</span>
            <span style={{ color: T.text, fontWeight: 600 }}>{formatTokens(seg.value)}</span>
            <span style={{ color: T.textMuted }}>({((seg.value / total) * 100).toFixed(1)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function InsightsGrid() {
  const s = summary.value;
  if (!s) return null;

  const totalTokens = s.totals.total_tokens || 1;
  const totalCost = s.totals.total_cost || 1;

  function renderList(items: Array<{ label: string; value: string; pct: number; color?: string }>) {
    return items.map((item, i) => (
      <div key={i}>
        <div style={S.insightRow}>
          <span style={S.insightLabel}>{item.label}</span>
          <span style={S.insightValue}>{item.value}</span>
        </div>
        <div style={S.barBg}>
          <div style={{ height: 4, borderRadius: 2, width: `${Math.max(2, item.pct)}%`, background: item.color || T.primary }} />
        </div>
      </div>
    ));
  }

  const metric = viewMode.value === 'cost' ? 'cost' : 'tokens';

  return (
    <div style={S.insightsGrid}>
      <div style={S.insightCard}>
 <div style={S.insightTitle}>Top Models</div>
        {renderList(s.by_model.map(m => ({
          label: m.model || 'unknown',
          value: metric === 'cost' ? formatUsd(m.cost) : formatTokens(m.tokens),
          pct: metric === 'cost' ? (m.cost / totalCost) * 100 : (m.tokens / totalTokens) * 100,
          color: T.purple,
        })))}
      </div>

      <div style={S.insightCard}>
 <div style={S.insightTitle}>Top Providers</div>
        {renderList(s.by_provider.map(p => ({
          label: p.provider || 'unknown',
          value: metric === 'cost' ? formatUsd(p.cost) : formatTokens(p.tokens),
          pct: metric === 'cost' ? (p.cost / totalCost) * 100 : (p.tokens / totalTokens) * 100,
          color: '#06b6d4',
        })))}
      </div>

      <div style={S.insightCard}>
 <div style={S.insightTitle}>Top Agents</div>
        {renderList(s.by_agent.map(a => ({
          label: a.agent || 'unknown',
          value: metric === 'cost' ? formatUsd(a.cost) : formatTokens(a.tokens),
          pct: metric === 'cost' ? (a.cost / totalCost) * 100 : (a.tokens / totalTokens) * 100,
          color: T.success,
        })))}
      </div>

      <div style={S.insightCard}>
 <div style={S.insightTitle}>Top Channels</div>
        {renderList(s.by_channel.map(c => ({
          label: c.channel || 'unknown',
          value: metric === 'cost' ? formatUsd(c.cost) : formatTokens(c.tokens),
          pct: metric === 'cost' ? (c.cost / totalCost) * 100 : (c.tokens / totalTokens) * 100,
          color: T.warning,
        })))}
      </div>

      <div style={S.insightCard}>
 <div style={S.insightTitle}>Stop Reasons</div>
        {renderList(s.by_stop_reason.map(sr => ({
          label: sr.stop_reason || 'unknown',
          value: String(sr.count),
          pct: (sr.count / s.totals.requests) * 100,
          color: sr.stop_reason === 'end_turn' ? T.success : sr.stop_reason === 'tool_use' ? T.danger : T.warning,
        })))}
      </div>

      <div style={S.insightCard}>
 <div style={S.insightTitle}>Top Tools</div>
        {s.top_tools.length > 0 ? renderList(s.top_tools.map(t => ({
          label: t.tool || 'unknown',
          value: String(t.count),
          pct: (t.count / s.totals.requests) * 100,
          color: T.danger,
        }))) : <div style={{ fontSize: 11, color: T.textMuted }}>No tool usage data yet</div>}
      </div>
    </div>
  );
}

function ActivityHeatmaps() {
  const s = summary.value;
  if (!s) return null;

  const maxHourly = Math.max(...s.hourly.map(h => h.tokens), 1);
  const maxDow = Math.max(...s.dow.map(d => d.tokens), 1);

  return (
    <div style={S.heatmapSection}>
      <div style={S.heatmapCard}>
 <div style={S.insightTitle}>Activity by Hour</div>
        <div style={{ ...S.heatmapGrid, gridTemplateColumns: 'repeat(12, 1fr)' }}>
          {Array.from({ length: 24 }, (_, h) => {
            const item = s.hourly.find(x => x.hour === h);
            const intensity = item ? item.tokens / maxHourly : 0;
            return (
              <div key={h} style={S.heatmapCell(intensity)} title={`${h}:00 — ${formatTokens(item?.tokens || 0)} tokens`}>
                {h}
              </div>
            );
          })}
        </div>
      </div>

      <div style={S.heatmapCard}>
 <div style={S.insightTitle}>Activity by Day</div>
        <div style={{ ...S.heatmapGrid, gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {DOW_LABELS.map((label, dow) => {
            const item = s.dow.find(x => x.dow === dow);
            const intensity = item ? item.tokens / maxDow : 0;
            return (
              <div key={dow} style={S.heatmapCell(intensity)} title={`${label} — ${formatTokens(item?.tokens || 0)} tokens`}>
                {label}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DailyChart() {
  const s = summary.value;
  if (!s || s.daily.length === 0) return null;

  const metric = viewMode.value;
  const maxVal = Math.max(...s.daily.map(d => metric === 'cost' ? d.cost : d.tokens), 1);

  return (
    <div style={S.dailyChart}>
 <div style={S.insightTitle}>Daily {metric === 'cost' ? 'Cost' : 'Tokens'}</div>
      <div style={S.barChart}>
        {s.daily.map((d, i) => {
          const val = metric === 'cost' ? d.cost : d.tokens;
          const h = (val / maxVal) * 100;
          return (
            <div
              key={i}
              style={S.bar(h, d.errors > 0 ? T.danger : T.primary)}
              title={`${d.date}\n${metric === 'cost' ? formatUsd(d.cost) : formatTokens(d.tokens)}\n${d.requests} reqs${d.errors > 0 ? `\n${d.errors} errors` : ''}`}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.textMuted, padding: '0 2px' }}>
        <span>{s.daily[0]?.date}</span>
        <span>{s.daily[s.daily.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function SessionsList() {
  const sortOptions: Array<{ value: string; label: string }> = [
 { value: 'cost', label: 'Cost' },
 { value: 'tokens', label: 'Tokens' },
 { value: 'requests', label: 'Requests' },
 { value: 'recent', label: 'Recent' },
  ];

  return (
    <div style={S.sessionsList}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
 <div style={S.sectionTitle}>Sessions ({sessionsTotal.value})</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {sortOptions.map(o => (
            <Button key={o.value} variant={sessionsSort.value === o.value ? 'primary' : 'ghost'} size="sm" onClick={() => { sessionsSort.value = o.value as any; loadAll(); }}>
              {o.label}
            </Button>
          ))}
        </div>
      </div>

      <div style={S.sessionHeader}>
        <span>Session / Machine</span>
        <span>Requests</span>
        <span>Tokens</span>
        <span>Cost</span>
        <span>Latency</span>
        <span>Errors</span>
      </div>

      {sessions.value.map(s => (
        <div
          key={s.session_key}
          style={S.sessionRow(selectedSession.value === s.session_key)}
          onClick={() => loadSessionDetail(s.session_key)}
        >
          <div>
            <div style={{ color: T.text, fontWeight: 500, fontSize: 12 }}>{s.session_key?.substring(0, 16) || '—'}</div>
            <div style={{ fontSize: 10, color: T.textMuted }}>
              {s.machine_name && <span style={S.badge(T.primary)}>{s.machine_name}</span>}
              {s.customer_email && <span style={{ color: T.textMuted }}>{s.customer_email}</span>}
            </div>
          </div>
          <span style={{ color: T.textSecondary }}>{s.requests.toLocaleString()}</span>
          <span style={{ color: T.primary }}>{formatTokens(s.tokens)}</span>
          <span style={{ color: T.success, fontWeight: 600 }}>{formatUsd(s.cost)}</span>
          <span style={{ color: T.textSecondary }}>{formatMs(s.avg_latency_ms)}</span>
          <span style={{ color: s.errors > 0 ? T.danger : T.textMuted }}>{s.errors}</span>
        </div>
      ))}

      {sessions.value.length === 0 && !loading.value && (
        <div style={{ textAlign: 'center', padding: 20, color: T.textMuted }}>No sessions found — data arrives when runners sync usage</div>
      )}
    </div>
  );
}

function SessionDrilldown() {
  if (!selectedSession.value) return null;

  return (
    <div style={S.drilldown}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={S.sectionTitle}>
 Session: {selectedSession.value?.substring(0, 20)}...
        </div>
        <Button variant="ghost" size="sm" onClick={() => { selectedSession.value = null; sessionLogs.value = []; sessionTimeseries.value = []; }}>
          Close
        </Button>
      </div>

      {sessionLoading.value && <div style={{ textAlign: 'center', padding: 20, color: T.textMuted }}>Loading...</div>}

      {sessionTimeseries.value.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 6 }}>Cumulative Tokens Over Time</div>
          <div style={{ ...S.barChart, height: 80 }}>
            {(() => {
              const maxT = Math.max(...sessionTimeseries.value.map(p => p.cumulative_tokens), 1);
              return sessionTimeseries.value.map((p, i) => (
                <div
                  key={i}
                  style={S.bar((p.cumulative_tokens / maxT) * 100, T.purple)}
                  title={`${p.timestamp}\nTokens: ${formatTokens(p.cumulative_tokens)}\nCost: ${formatUsd(p.cumulative_cost)}`}
                />
              ));
            })()}
          </div>
        </div>
      )}

      {sessionLogs.value.length > 0 && (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <div style={{ ...S.logRow(false), color: T.textMuted, fontWeight: 600, borderBottom: `1px solid ${T.borderHover}` }}>
            <span>Time</span>
            <span>Model</span>
            <span>Input</span>
            <span>Output</span>
            <span>Cost</span>
            <span>Duration</span>
          </div>
          {sessionLogs.value.map(log => (
            <div key={log.id} style={S.logRow(log.error_count > 0)}>
              <span style={{ color: T.textSecondary }}>{new Date(log.created_at).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: '2-digit' })}</span>
              <span>
                <span style={S.badge(T.purple)}>{log.model?.split('/').pop() || log.model}</span>
 {log.tool_call_count > 0 && <span style={S.badge(T.danger)}>{log.tool_call_count}</span>}
              </span>
              <span style={{ color: T.primary }}>{formatTokens(log.input_tokens || 0)}</span>
              <span style={{ color: T.success }}>{formatTokens(log.output_tokens || 0)}</span>
              <span style={{ color: T.warning }}>{formatUsd(log.cost_total || 0)}</span>
              <span style={{ color: T.textSecondary }}>{formatMs(log.duration_ms || 0)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════

export function AdminUsagePage() {
  useEffect(() => { loadAll(); }, []);

  if (loading.value && !summary.value) {
    return (
      <div style={S.page}>
 <div style={S.h1}>Usage Dashboard</div>
        <div style={{ textAlign: 'center', padding: 60, color: T.textMuted }}>Loading usage data...</div>
      </div>
    );
  }

  return (
    <div style={S.page}>
 <div style={S.h1}>Usage Dashboard</div>
      <div style={S.subtitle}>
        LLM usage analytics — data from OpenClaw runner sync (llm_usage_logs)
      </div>

      {error.value && (
        <div style={{ background: T.dangerBg, border: `1px solid ${T.danger}`, borderRadius: 8, padding: 12, marginBottom: 16, color: T.danger, fontSize: 13 }}>
 {error.value}
        </div>
      )}

      <FilterBar />
      <OverviewCards />
      <TokensByTypeChart />
      <DailyChart />
      <ActivityHeatmaps />
      <InsightsGrid />
      <SessionsList />
      <SessionDrilldown />
    </div>
  );
}
